import type { AttributeFilters } from '../attributeFilters';
import type { SnapshotId } from '../snapshot';
import type { NonEmptyArray } from '../types';
import type { TableColumnSort } from '../user/index';
import type { TableValueFormattingContext } from './columns';
import type { Filters } from './filters';

export type SnapshotRequestShortcut = '$prev' | '$last' | '$lastLocked';
export type TableRequestColumns<TRow> = NonEmptyArray<keyof TRow & string>;

export interface TableDataFormatOptions extends TableValueFormattingContext {}

export const tableDataTypes = ['json', 'csv'] as const;
export type TableDataType = (typeof tableDataTypes)[number];

export interface TableDataFormat {
  dataType?: TableDataType;
  exportToFile?: boolean | string;
  options?: TableDataFormatOptions;
}

/**
 * Request body type common for all table endpoints
 *
 * ## bindVariables
 * There is one special field specific for some tables that is not typed there
 * on purpose - `bindVariables`. It should be a key-value object with specific shape
 * for every such table endpoint. If you need to use it, you have to specify
 * request body type for given table endpoint:
 *
 * ```
 * type UserTableRequestBody = TableRequestBody<UserRow> & {
 *   bindVariables: {admin: boolean};
 * };
 * ```
 *
 * We want to enforce the type definition every time variables are used to avoid mistakes.
 * The best way to do that is not type it there; if we'd use e.g. `bindVariables?: unknown`,
 * it would allow passing any value in definitions.
 *
 */
export type TableRequestBody<TRow> = {
  bindVariables?: Record<string, unknown>;
  columns: TableRequestColumns<TRow>;
  filters: Filters;
  attributeFilters?: AttributeFilters;
  pagination?: {
    limit: number;
    start: number;
  };
  snapshot?: SnapshotId | SnapshotRequestShortcut;
  sort?: TableColumnSort<keyof TRow & string>;
  format?: TableDataFormat;
};

export interface TableResponseBase {
  _meta: {
    /** Total number of rows in the table with currently applied set of filters without pagination */
    count: number;
    /**
     * ID of actual snapshot
     *
     * Useful when SnapshotRequestShortcut is used in request; otherwise it is equal to request.snapshot.
     */
    snapshot?: SnapshotId;

    // following columns are just for easier human reading of response,
    // can be determined from request or response data
    /** Corresponds to pagination?.limit ?? null from table request */
    limit: number | null;
    /** Number of rows returned in this request with pagination applied, corresponds to data.length */
    size: number;
    /** Corresponds to pagination?.start ?? 0 from table request */
    start: number;
  };
}

export interface TableResponseBody<TRow> extends TableResponseBase {
  data: TRow[];
}
